import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Code2,
  BarChart3,
  Cloud,
  ArrowRight,
  Users,
  Rocket,
  Shield,
  Lightbulb,
  Target,
  CheckCircle,
  TrendingUp,
  FileText,
  MessageSquare,
  Calendar,
  Zap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ClientMarquee from "@/components/ClientMarquee";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO from "@/components/SEO";

const stats = [
  { value: "1M+", label: "Users Engaged" },
  { value: "32", label: "Active AI Projects" },
  { value: "50+", label: "Global Clients" },
];

const capabilities = [
  {
    icon: Brain,
    title: "AI & Machine Learning",
    description: "Generative AI (LLMs, VLMs), Computer Vision, NLP & Conversational AI, Predictive Analytics, Responsible AI",
    gradientClass: "from-blue-600 via-indigo-500 to-violet-600",
    graphicText: "Sovereign AI",
    patternType: "neural",
    link: "/expertise"
  },
  {
    icon: Code2,
    title: "Full-Stack Engineering",
    description: "Mobile Apps (iOS/Android/Flutter), Web Applications, Backend & APIs, Cloud-native architecture, UI/UX systems",
    gradientClass: "from-amber-50 via-orange-500 to-rose-500",
    graphicText: "Web & Mobile",
    patternType: "grid",
    link: "/services"
  },
  {
    icon: BarChart3,
    title: "Analytics & Data",
    description: "Power BI, Tableau, Looker, Data Warehousing & ETL, Real-time dashboards, BI Strategy",
    gradientClass: "from-emerald-500 via-teal-500 to-cyan-500",
    graphicText: "Data Scale",
    patternType: "ripple",
    link: "/services"
  },
  {
    icon: Cloud,
    title: "Cloud & DevOps",
    description: "AWS, Azure, GCP, Hybrid deployments, CI/CD, Kubernetes, Terraform, Microservices",
    gradientClass: "from-purple-600 via-fuchsia-500 to-rose-500",
    graphicText: "Cloud Scale",
    patternType: "wave",
    link: "/expertise"
  },
];

const whyUs = [
  { icon: Target, title: "Sector-agnostic expertise", description: "Deep experience across industries" },
  { icon: Users, title: "Consulting-led execution", description: "Strategic approach to every project" },
  { icon: Lightbulb, title: "AI-first approach", description: "Innovation at the core of solutions" },
  { icon: Shield, title: "Enterprise-ready partnership", description: "Scalable, secure implementations" },
];

const offerings = [
  {
    title: "Full-stack AI Product Development",
    description: "End-to-end AI solution development from concept to production",
    icon: Rocket,
  },
  {
    title: "Workforce Augmentation",
    description: "Skilled AI and engineering talent to enhance your team",
    icon: Users,
  },
  {
    title: "AI Consulting",
    description: "Strategic guidance to maximize AI investment returns",
    icon: Lightbulb,
  },
];

const caseStudyHighlights = [
  {
    id: "sales-operations-digitization",
    icon: TrendingUp,
    title: "Sales & Operations Digitization",
    client: "Global Smartphone Brand",
    description: "Digitizing Sales and Operations Management through an end-to-end centralized platform, eliminating manual reconciliation.",
    tags: ["S&OP", "Digital Transformation", "Analytics"],
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    metric: "Eliminated Manual Reconciliation",
  },
  {
    id: "ai-invoicing-engine",
    icon: FileText,
    title: "AI Invoicing Engine",
    client: "Global Enterprise",
    description: "AI-powered invoicing system that automates financial workflows and handles massive volumes of invoices in multiple formats.",
    tags: ["IDP", "Computer Vision", "Finance"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    metric: "Eliminated Invoice Backlog",
  },
  {
    id: "event-brief-automation",
    icon: Calendar,
    title: "Event Brief Automation",
    client: "Events Management Company",
    description: "AI system to automate event brief creation and approvals, transforming manual document-based workflows.",
    tags: ["NLP", "Document AI", "Workflow"],
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    metric: "70% Faster Brief Generation",
  },
  {
    id: "whatsapp-ai-pitch-bot",
    icon: MessageSquare,
    title: "WhatsApp AI Pitch Bot",
    client: "Startup Ecosystem",
    description: "AI-powered WhatsApp bot enabling regional founders to create professional pitch decks, breaking language barriers.",
    tags: ["Generative AI", "WhatsApp API", "Multilingual"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    metric: "Massive Founder Onboarding",
  },
];

const homeJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://vanco.ai/#organization",
    name: "VANCO",
    alternateName: "VANCO AI",
    legalName: "Codebugged Organization Private Limited",
    url: "https://vanco.ai/",
    sameAs: ["https://www.linkedin.com/company/vanco-ai/"],
    knowsAbout: ["AI services", "AI implementation", "AI agents", "Generative AI", "Workflow automation", "Computer vision", "Document AI"],
    logo: "https://vanco.ai/og-image.png",
    description:
      "VANCO is an AI services and AI implementation company building custom AI agents, automation and full-stack software for enterprises.",
    address: [
      {
        "@type": "PostalAddress",
        streetAddress: "4th Floor, Block A, BHIVE Premium HSR Campus, AKR Tech Park, 7th Mile, Hosur Road, Krishna Reddy Industrial Area",
        addressLocality: "Bengaluru",
        addressRegion: "Karnataka",
        postalCode: "560068",
        addressCountry: "IN",
      },
      {
        "@type": "PostalAddress",
        streetAddress: "2604 Whittier Place",
        addressLocality: "Wilmington",
        addressRegion: "DE",
        postalCode: "19808",
        addressCountry: "US",
      },
    ],
    parentOrganization: {
      "@type": "Organization",
      name: "Codebugged",
      url: "https://codebugged.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://vanco.ai/#website",
    name: "VANCO",
    url: "https://vanco.ai/",
    publisher: { "@id": "https://vanco.ai/#organization" },
  },
];

export default function HomePage() {
  return (
    <div className="overflow-hidden bg-white" data-testid="home-page">
      <SEO
        title={"VANCO | AI Services & AI Implementation Company in India"}
        description={"VANCO is an AI services and AI implementation company in Bengaluru, India. We build AI agents, generative AI and automation that run in production for enterprises."}
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex flex-col justify-between pt-32 pb-6 bg-gradient-to-b from-[#f8fafc]/60 to-white" data-testid="hero-section">
        {/* Static Premium Grid Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Masked Infinite CSS Grid Pattern - Made highly subtle to maximize background colors */}
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: "linear-gradient(to right, rgba(36, 59, 127, 0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(36, 59, 127, 0.03) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
              maskImage: "radial-gradient(circle at 50% 30%, black 20%, transparent 85%)",
              WebkitMaskImage: "radial-gradient(circle at 50% 30%, black 20%, transparent 85%)"
            }}
          />

          {/* Static geometric grid cells - Made highly subtle */}
          <div className="absolute inset-0 grid grid-cols-6 md:grid-cols-12 gap-0 opacity-[0.03] pointer-events-none">
            {Array.from({ length: 72 }).map((_, i) => {
              const staticOpacity = [0.03, 0.06, 0.08, 0.04][i % 4];
              const hasGoldBg = i % 7 === 0;
              return (
                <div
                  key={i}
                  className="border-[0.5px] border-[#243b7f]/5 aspect-square"
                  style={{
                    opacity: staticOpacity,
                    backgroundColor: hasGoldBg ? "rgba(247, 197, 80, 0.02)" : "transparent"
                  }}
                />
              );
            })}
          </div>

          {/* Static glassmorphic color blobs in the background (Aurora Mesh) - High Contrast & Rich Colors */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Center-Top Blob: Highly Vibrant Rich Orange-Yellow Glow */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-64 w-[1100px] h-[500px] rounded-full bg-gradient-to-b from-[#f7c550]/75 via-[#fbbf24]/45 to-transparent blur-[100px]" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-40 w-[500px] h-[250px] rounded-full bg-[#f7c550]/35 blur-[80px]" />

            {/* Left Blob: Extremely Soft, Light Sky Blue / Pastel Indigo - Pulled closer to the center */}
            <div className="absolute left-[18%] -translate-x-1/2 -top-20 w-[800px] h-[550px] rounded-full bg-gradient-to-br from-[#60a5fa]/30 via-[#818cf8]/20 to-transparent blur-[105px]" />

            {/* Right Blob: Extremely Soft, Light Lavender / Sky Blue - Pulled closer to the center */}
            <div className="absolute right-[18%] translate-x-1/2 -top-20 w-[800px] h-[550px] rounded-full bg-gradient-to-br from-[#c084fc]/25 via-[#93c5fd]/20 to-transparent blur-[105px]" />
          </div>
        </div>

        {/* Background decorations - overlaid on top with low opacity */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#243b7f]/5 rounded-full blur-3xl" />
        </div>

        {/* Hero Content Container - Centered vertically */}
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-20 py-8 md:py-12 flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex flex-col items-center"
            >
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-block px-4 py-1.5 border border-[#243b7f]/20 rounded-full text-[#243b7f] text-xs font-semibold mb-4 tracking-wider uppercase bg-white/40 backdrop-blur-sm font-season-mix"
              >
                AI Partner for the Enterprise
              </motion.span>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0f172a] font-season-mix mb-4 max-w-3xl mx-auto">
                Empowering enterprises to scale{" "}
                <span className="text-[#243b7f]">smarter</span>,{" "}
                <span className="relative inline-block">
                  faster
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 10C50 4 150 4 198 10" stroke="#f7c550" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-6 max-w-2xl mx-auto">
                With AI that drives meaningful impact. Through our{" "}
                <Link to="/ai-services" className="text-[#243b7f] underline-offset-4 hover:underline">AI services</Link> and{" "}
                <Link to="/ai-implementation" className="text-[#243b7f] underline-offset-4 hover:underline">AI implementation</Link>{" "}
                work, we partner with enterprises to reimagine processes, unlock value, and deliver production-ready AI solutions.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/services">
                  <Button
                    size="lg"
                    className="bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-900/20"
                    data-testid="hero-cta-explore"
                  >
                    Explore VANCO
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[#243b7f]/30 text-[#243b7f] hover:bg-[#243b7f]/5 rounded-full px-8 py-6 text-lg font-medium bg-white/80 backdrop-blur"
                    data-testid="hero-cta-contact"
                  >
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Client Marquee - Integrated inside the hero section to share the background mesh and prevent any line seam, pushed down */}
        <div className="relative z-20 w-full mt-20 pb-4">
          <ClientMarquee />
        </div>
      </section>

      {/* About Snapshot */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="about-snapshot">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn direction="left">
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                About VANCO
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                At VANCO, we go beyond code
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We partner with enterprises to reimagine processes, unlock value via automation, and deliver full-stack AI solutions that create real, measurable impact.
              </p>
              <ul className="space-y-4">
                {[
                  "Reimagine processes with AI-first thinking",
                  "Unlock value via intelligent automation",
                  "Deliver full-stack AI solutions"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-[#243b7f] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>

            <FadeIn direction="right" delay={0.2}>
              <div className="grid grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className="text-center p-6 bg-slate-50 rounded-2xl"
                  >
                    <div className="text-4xl md:text-5xl font-bold text-[#243b7f] font-['Manrope'] mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-500 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </AnimatedSection>

      {/* Capabilities Section */}
      <AnimatedSection className="py-24 md:py-32 bg-slate-50" data-testid="capabilities-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                What We Do
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                Our Capabilities
              </h2>
              <p className="text-lg text-slate-600">
                End-to-end technology solutions to transform your business
              </p>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.map((cap, index) => (
              <AnimatedItem key={cap.title}>
                <Card className="group h-full bg-white border border-slate-100/80 rounded-3xl p-6 hover:shadow-2xl hover:shadow-slate-200/40 transition-all duration-500 hover:-translate-y-1.5 flex flex-col justify-between overflow-hidden">
                  {/* Top Text Content */}
                  <div className="flex flex-col text-left mb-6">
                    {/* Category with icon */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 rounded-lg bg-slate-50 text-slate-500 group-hover:bg-[#243b7f]/10 group-hover:text-[#243b7f] transition-colors duration-300">
                        <cap.icon className="w-4 h-4" />
                      </div>
                      <span className="text-xs uppercase tracking-widest font-extrabold text-slate-400 font-season-mix">
                        Capability
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight font-season-mix mb-2 group-hover:text-[#243b7f] transition-colors leading-tight">
                      {cap.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                      {cap.description}
                    </p>

                    <Link to={cap.link} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#243b7f] hover:text-[#f7c550] transition-colors mt-auto">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {/* Bottom Graphic Container */}
                  <div className={`relative h-44 w-full rounded-2xl overflow-hidden bg-gradient-to-br ${cap.gradientClass} flex items-center justify-center shadow-inner`}>
                    {/* Decorative glassmorphic shapes inside the graphic */}
                    <div className="absolute inset-0 opacity-25 mix-blend-overlay">
                      <div className="absolute -top-10 -left-10 w-32 h-32 bg-white rounded-full blur-xl" />
                      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-black rounded-full blur-xl" />
                    </div>

                    {/* Custom background pattern overlays based on patternType */}
                    {cap.patternType === "neural" && (
                      <div className="absolute inset-0 overflow-hidden opacity-25">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-white/20 border-dashed" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border border-white/50" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-white/30 border-dashed" />
                        <div className="absolute top-10 left-10 w-2 h-2 rounded-full bg-white" />
                        <div className="absolute top-12 left-24 w-1.5 h-1.5 rounded-full bg-white/80" />
                        <div className="absolute bottom-10 right-12 w-2.5 h-2.5 rounded-full bg-white/70" />
                        <div className="absolute bottom-16 left-20 w-2 h-2 rounded-full bg-white/90" />
                      </div>
                    )}

                    {cap.patternType === "grid" && (
                      <div className="absolute inset-0 overflow-hidden opacity-20">
                        <div className="absolute inset-0 grid grid-cols-6 gap-2 p-4">
                          {Array.from({ length: 12 }).map((_, i) => (
                            <div key={i} className="border border-white/40 rounded-sm aspect-square" />
                          ))}
                        </div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-2xl border border-white/60 rotate-45" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-xl border border-white/40 rotate-45" />
                      </div>
                    )}

                    {cap.patternType === "ripple" && (
                      <div className="absolute inset-0 overflow-hidden opacity-25">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 rounded-full border-2 border-dashed border-white/30" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border border-white/50" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full border border-white/70" />
                      </div>
                    )}

                    {cap.patternType === "wave" && (
                      <div className="absolute inset-0 overflow-hidden opacity-25">
                        <div className="absolute -bottom-10 -left-10 w-48 h-32 bg-white/20 rounded-full blur-md" />
                        <div className="absolute -top-10 -right-10 w-48 h-32 bg-white/20 rounded-full blur-md" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full border border-white/50" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-16 rounded-full border border-white/30" />
                      </div>
                    )}

                    {/* Graphic Icon Overlay */}
                    <cap.icon className="relative z-10 w-12 h-12 text-white drop-shadow-md" strokeWidth={1.5} />
                  </div>
                </Card>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Why Us Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="why-us-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <FadeIn>
                <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                  Why VANCO
                </span>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                  Why choose us?
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We combine deep technical expertise with strategic consulting to deliver AI solutions that truly transform businesses.
                </p>
              </FadeIn>
            </div>

            <div className="lg:col-span-3">
              <AnimatedContainer className="grid sm:grid-cols-2 gap-6">
                {whyUs.map((item, index) => (
                  <AnimatedItem key={item.title}>
                    <div className="p-6 rounded-2xl bg-slate-50 hover:bg-[#243b7f] group transition-all duration-300 cursor-default">
                      <item.icon className="w-10 h-10 text-[#243b7f] group-hover:text-[#f7c550] mb-4 transition-colors" />
                      <h3 className="text-lg font-semibold text-[#0f172a] group-hover:text-white font-['Manrope'] mb-2 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600 group-hover:text-white/80 transition-colors">
                        {item.description}
                      </p>
                    </div>
                  </AnimatedItem>
                ))}
              </AnimatedContainer>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Offerings Section */}
      <AnimatedSection className="py-24 md:py-32 bg-[#243b7f]" data-testid="offerings-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Our Offerings
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white font-['Manrope'] mb-6">
                How We Can Help
              </h2>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid md:grid-cols-3 gap-8">
            {offerings.map((offering, index) => (
              <AnimatedItem key={offering.title} className="h-full">
                <div className="h-full flex flex-col text-center p-8 rounded-3xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="w-16 h-16 rounded-full bg-[#f7c550] flex items-center justify-center mx-auto mb-6 flex-shrink-0">
                    <offering.icon className="w-8 h-8 text-[#0f172a]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white font-['Manrope'] mb-3 flex-shrink-0">
                    {offering.title}
                  </h3>
                  <p className="text-white/70 flex-grow">
                    {offering.description}
                  </p>
                </div>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Case Study Highlights */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="case-studies-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Success Stories
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope']">
                Case Study Highlights
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <Link to="/case-studies">
                <Button
                  variant="outline"
                  className="border-[#243b7f]/30 text-[#243b7f] hover:bg-[#243b7f]/5 rounded-full px-6"
                  data-testid="view-all-case-studies"
                >
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseStudyHighlights.map((study, index) => (
              <AnimatedItem key={study.title}>
                <Link to={`/case-studies/${study.id}`}>
                  <Card className="h-full bg-white border-slate-100 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer group overflow-hidden">
                    <CardContent className="p-0">
                      {/* Header with icon */}
                      <div className={`${study.color} p-5 relative overflow-hidden`}>
                        <div className="flex items-start justify-between">
                          <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                            <study.icon className={`w-6 h-6 ${study.iconColor}`} />
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#243b7f] group-hover:translate-x-1 transition-all duration-300" />
                        </div>

                        {/* Metric badge */}
                        <div className="mt-4">
                          <span className="inline-block px-3 py-1 bg-white/80 rounded-full text-xs font-bold text-[#243b7f] uppercase tracking-wider">
                            {study.metric}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <p className="text-xs text-slate-500 font-medium mb-2">{study.client}</p>
                        <h3 className="text-lg font-bold text-[#0f172a] font-['Manrope'] mb-2 group-hover:text-[#243b7f] transition-colors leading-tight">
                          {study.title}
                        </h3>
                        <p className="text-slate-600 text-xs leading-relaxed mb-4 line-clamp-2">
                          {study.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {study.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="bg-slate-50 text-slate-500 text-xs px-2 py-0 border-0">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-50 to-blue-50" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
              Ready to build AI that works?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help transform your business with intelligent solutions.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
                data-testid="footer-cta"
              >
                Let's Talk
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
